'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { type SanityImageProps, resolveAlt, resolveImageA11y, getImageDimensions, buildImageSrc, getLqip, computeDefaultSizes, parseAspectRatio } from './SanityImage.shared';

/**
 * Client-rendered SanityImage with error fallback.
 * Uses next/image with Sanity CDN loader for optimized delivery.
 * next/image handles blur placeholder transitions natively.
 * For static images without interactivity, prefer SanityImageServer.
 */
export function SanityImage(t0) {
  const $ = _c(63);
  const {
    image,
    width: widthProp,
    height: heightProp,
    className,
    fill: fillProp,
    alt: altProp,
    priority: t1,
    sizes: sizesProp,
    aspectRatio,
    quality: t2,
    placeholder: t3
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const quality = t2 === undefined ? 80 : t2;
  const placeholder = t3 === undefined ? "blur" : t3;
  const [hasError, setHasError] = useState(false);
  if (!image.asset?._id) {
    return null;
  }
  let alt;
  let t4;
  if ($[0] !== altProp || $[1] !== image) {
    alt = resolveAlt(altProp, image);
    t4 = resolveImageA11y(alt);
    $[0] = altProp;
    $[1] = image;
    $[2] = alt;
    $[3] = t4;
  } else {
    alt = $[2];
    t4 = $[3];
  }
  const a11yProps = t4;
  let height;
  let parsedRatio;
  let t5;
  let width;
  if ($[4] !== aspectRatio || $[5] !== heightProp || $[6] !== image || $[7] !== quality || $[8] !== widthProp) {
    const intrinsic = getImageDimensions(image);
    parsedRatio = parseAspectRatio(aspectRatio);
    width = widthProp ?? intrinsic.width;
    const effectiveRatio = parsedRatio ?? intrinsic.aspectRatio;
    height = heightProp ?? (width && effectiveRatio ? Math.round(width / effectiveRatio) : intrinsic.height);
    t5 = buildImageSrc(image, {
      width: width ?? 1200,
      height,
      quality
    });
    $[4] = aspectRatio;
    $[5] = heightProp;
    $[6] = image;
    $[7] = quality;
    $[8] = widthProp;
    $[9] = height;
    $[10] = parsedRatio;
    $[11] = t5;
    $[12] = width;
  } else {
    height = $[9];
    parsedRatio = $[10];
    t5 = $[11];
    width = $[12];
  }
  const src = t5;
  let t6;
  if ($[13] !== image) {
    t6 = getLqip(image);
    $[13] = image;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const lqip = t6;
  let t7;
  if ($[15] !== fillProp || $[16] !== sizesProp || $[17] !== width) {
    t7 = computeDefaultSizes(sizesProp, fillProp, width);
    $[15] = fillProp;
    $[16] = sizesProp;
    $[17] = width;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const sizes = t7;
  let t8;
  if ($[19] !== lqip || $[20] !== placeholder) {
    t8 = placeholder === "blur" && lqip ? {
      placeholder: "blur" as const,
      blurDataURL: lqip
    } : {};
    $[19] = lqip;
    $[20] = placeholder;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const placeholderProps = t8;
  const resolvedWidth = width ?? 1200;
  let t9;
  if ($[22] !== height || $[23] !== parsedRatio || $[24] !== resolvedWidth) {
    t9 = height ?? (parsedRatio ? Math.round(resolvedWidth / parsedRatio) : 800);
    $[22] = height;
    $[23] = parsedRatio;
    $[24] = resolvedWidth;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const resolvedHeight = t9;
  if (fillProp) {
    let t10;
    if ($[26] !== className) {
      t10 = cn("relative overflow-hidden absolute inset-0", className);
      $[26] = className;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== a11yProps || $[29] !== alt || $[30] !== hasError || $[31] !== placeholderProps || $[32] !== priority || $[33] !== quality || $[34] !== sizes || $[35] !== src) {
      t11 = hasError ? <ErrorFallback /> : <Image src={src} alt={alt} fill={true} priority={priority} sizes={sizes} quality={quality} onError={() => setHasError(true)} className="object-cover" {...a11yProps} {...placeholderProps} />;
      $[28] = a11yProps;
      $[29] = alt;
      $[30] = hasError;
      $[31] = placeholderProps;
      $[32] = priority;
      $[33] = quality;
      $[34] = sizes;
      $[35] = src;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] !== t10 || $[38] !== t11) {
      t12 = <div className={t10}>{t11}</div>;
      $[37] = t10;
      $[38] = t11;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    return t12;
  }
  if (hasError) {
    let t10;
    if ($[40] !== className) {
      t10 = cn("relative overflow-hidden", className);
      $[40] = className;
      $[41] = t10;
    } else {
      t10 = $[41];
    }
    let t11;
    if ($[42] !== resolvedHeight || $[43] !== resolvedWidth) {
      t11 = {
        width: resolvedWidth,
        height: resolvedHeight
      };
      $[42] = resolvedHeight;
      $[43] = resolvedWidth;
      $[44] = t11;
    } else {
      t11 = $[44];
    }
    let t12;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <ErrorFallback />;
      $[45] = t12;
    } else {
      t12 = $[45];
    }
    let t13;
    if ($[46] !== t10 || $[47] !== t11) {
      t13 = <div className={t10} style={t11}>{t12}</div>;
      $[46] = t10;
      $[47] = t11;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    return t13;
  }
  let t10;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setHasError(true);
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== className) {
    t11 = cn("max-w-full h-auto", className);
    $[50] = className;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== a11yProps || $[53] !== alt || $[54] !== placeholderProps || $[55] !== priority || $[56] !== quality || $[57] !== resolvedHeight || $[58] !== resolvedWidth || $[59] !== sizes || $[60] !== src || $[61] !== t11) {
    t12 = <Image src={src} alt={alt} width={resolvedWidth} height={resolvedHeight} priority={priority} sizes={sizes} quality={quality} onError={t10} className={t11} {...a11yProps} {...placeholderProps} />;
    $[52] = a11yProps;
    $[53] = alt;
    $[54] = placeholderProps;
    $[55] = priority;
    $[56] = quality;
    $[57] = resolvedHeight;
    $[58] = resolvedWidth;
    $[59] = sizes;
    $[60] = src;
    $[61] = t11;
    $[62] = t12;
  } else {
    t12 = $[62];
  }
  return t12;
}

/**
 * Error fallback UI shown when the image fails to load.
 */
function ErrorFallback() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted/50 to-muted" aria-hidden="true"><svg className="h-12 w-12 text-muted-foreground/30" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}